import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { normalizeError } from '@/lib/error-types';

/**
 * Hook to ensure Supabase Realtime has auth context before creating subscriptions.
 * Surfaces async errors to the nearest ErrorBoundary via render-phase re-throw.
 */
export function useRealtimeAuth() {
  const [isRealtimeReady, setIsRealtimeReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const initializeRealtimeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (session?.access_token) {
          supabase.realtime.setAuth(session.access_token);
          logger.debug('[useRealtimeAuth] Realtime auth token set', {
            userId: session.user.id,
            timestamp: new Date().toISOString()
          });
        } else {
          logger.debug('[useRealtimeAuth] No session - anonymous realtime access');
        }
        
        setIsRealtimeReady(true);
      } catch (err) {
        logger.error('[useRealtimeAuth] CRITICAL: Failed to initialize realtime auth', { error: err });
        if (mounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    };

    initializeRealtimeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Re-throw during render so the nearest ErrorBoundary catches it
  if (error) {
    throw normalizeError(error);
  }

  return isRealtimeReady;
}
